import {
	Modal,
	ModalContent,
	ModalFooter,
	ModalTitle,
	ModalWrapper,
	PrimaryButton,
	SPACING,
	SecondaryButton,
	useModal,
	BodyL,
} from '@monthio/design-system'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useCallback } from 'react'

const StyledModalContent = styled(ModalContent)`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.l};
	padding-block-start: calc(${SPACING.m} + 30px);
`
const Paragraph = styled(BodyL)`
	margin: 0;
`

interface AccessibleAccountsPromptProps {
	onOpenChange: (isOpen: boolean) => void
	clientName: string
	banksToAdd: string[]
	handleContinue: () => void
	handleAddMoreBanks: () => void
}

export const AccessibleAccountsPrompt = ({
	onOpenChange,
	clientName,
	banksToAdd,
	handleContinue,
	handleAddMoreBanks,
}: AccessibleAccountsPromptProps) => {
	return (
		<Modal isOpen onOpenChange={onOpenChange}>
			<AccountsPromptWrapper
				clientName={clientName}
				handleContinue={handleContinue}
				handleAddMoreBanks={handleAddMoreBanks}
				banksToAdd={banksToAdd}
			/>
		</Modal>
	)
}

interface AccountsPromptWrapperProps {
	clientName: string
	banksToAdd: string[]
	handleContinue: () => void
	handleAddMoreBanks: () => void
}

const AccountsPromptWrapper = ({
	clientName,
	handleContinue,
	handleAddMoreBanks,
	banksToAdd,
}: AccountsPromptWrapperProps) => {
	const { t } = useTranslation()
	const { close } = useModal()

	const getBankModalBody = (banksToAdd: string[]) => {
		if (banksToAdd.length === 0) {
			return (
				<StyledModalContent>
					<ModalTitle>{t('prompt-more-accounts-title')}</ModalTitle>
					<Paragraph>
						{t('prompt-more-accounts-body-text1', { clientName })}
					</Paragraph>
					<Paragraph>{t('prompt-more-accounts-body-text2')}</Paragraph>
				</StyledModalContent>
			)
		}
		return (
			<StyledModalContent>
				<ModalTitle>{t('prompt-more-banks-title')}</ModalTitle>
				<Paragraph>
					{banksToAdd.length == 1
						? t('prompt-body-missing-accounts-single-bank-missing', {
								bankName: banksToAdd[0],
							})
						: t('prompt-body-missing-accounts-multiple-banks-missing', {
								bankList: banksToAdd.slice(0, -1).join(', '),
								lastBank: banksToAdd.slice(-1)[0],
							})}
				</Paragraph>
			</StyledModalContent>
		)
	}

	const continueAction = useCallback(() => {
		handleContinue()
		close()
	}, [])
	return (
		<ModalWrapper>
			{getBankModalBody(banksToAdd)}
			<ModalFooter orientation="vertical">
				<PrimaryButton onClick={handleAddMoreBanks} size="xl">
					{t('prompt-add-more-banks-accounts')}
				</PrimaryButton>
				<SecondaryButton onClick={continueAction} size="xl" variant="color">
					{t('prompt-correct-continue')}
				</SecondaryButton>
			</ModalFooter>
		</ModalWrapper>
	)
}
